import type { DebugBridge } from './DebugBridge';

export interface MemoryDumpLine {
  address: number;
  bytes: number[];
  ascii: string;
}

const REGION_BOUNDS: Array<{ start: number; end: number; color: string; className: string }> = [
  { start: 0xD180, end: 0xFFFF, color: '#e0ffe0', className: 'mem-ram-var' },
  { start: 0xD000, end: 0xFFFF, color: '#c8ffd6', className: 'mem-ram' },
  { start: 0xFF0000, end: 0xFFFFFF, color: '#ffc8c8', className: 'mem-peripheral' },
  { start: 0x000000, end: 0x0F0000, color: '#c8d6ff', className: 'mem-code' },
];

function getRegionColor(address: number): string {
  for (const region of REGION_BOUNDS) {
    if (address >= region.start && address <= region.end) {
      return region.color;
    }
  }
  return '';
}

export function getRegionClass(address: number): string {
  for (const region of REGION_BOUNDS) {
    if (address >= region.start && address <= region.end) {
      return region.className;
    }
  }
  return '';
}

export class MemoryView {
  readonly el: HTMLDivElement;
  private bridge: DebugBridge;
  private address: number = 0;
  private lines: MemoryDumpLine[] = [];
  private autoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private autoRefreshInterval: number = 1000;
  private gotoInput: HTMLInputElement;
  private dumpEl: HTMLDivElement;
  private autoRefreshBtn: HTMLButtonElement;

  constructor(bridge: DebugBridge) {
    this.bridge = bridge;

    this.el = document.createElement('div');
    this.el.className = 'memory-view';

    const toolbar = document.createElement('div');
    toolbar.className = 'memory-toolbar';

    const addrLabel = document.createElement('span');
    addrLabel.textContent = 'Address:';
    toolbar.appendChild(addrLabel);

    this.gotoInput = document.createElement('input');
    this.gotoInput.type = 'text';
    this.gotoInput.placeholder = '0x000000';
    this.gotoInput.className = 'memory-goto-input';
    this.gotoInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleGoto();
    });
    toolbar.appendChild(this.gotoInput);

    const gotoBtn = document.createElement('button');
    gotoBtn.textContent = 'Go';
    gotoBtn.className = 'memory-goto-btn';
    gotoBtn.onclick = () => this.handleGoto();
    toolbar.appendChild(gotoBtn);

    this.autoRefreshBtn = document.createElement('button');
    this.autoRefreshBtn.textContent = 'Auto Refresh: OFF';
    this.autoRefreshBtn.className = 'memory-autorefresh-btn';
    this.autoRefreshBtn.onclick = () => this.setAutoRefresh(!this.autoRefreshTimer);
    toolbar.appendChild(this.autoRefreshBtn);

    this.el.appendChild(toolbar);

    this.dumpEl = document.createElement('div');
    this.dumpEl.className = 'memory-dump';
    this.el.appendChild(this.dumpEl);
  }

  private handleGoto(): void {
    let addr = parseInt(this.gotoInput.value.trim(), 16);
    if (isNaN(addr)) addr = 0;
    this.goToAddress(addr);
  }

  async readMemory(address: number, length: number): Promise<Uint8Array> {
    return this.bridge.readMemory(address, length);
  }

  async goToAddress(address: number): Promise<void> {
    this.address = address;
    const data = await this.readMemory(address, 256);
    this.lines = this.buildDumpLines(address, data);
    this.update();
  }

  async writeMemory(address: number, data: Uint8Array): Promise<void> {
    await this.bridge.writeMemory(address, data);
  }

  setAutoRefresh(enabled: boolean, intervalMs?: number): void {
    if (intervalMs !== undefined) {
      this.autoRefreshInterval = intervalMs;
    }

    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
    }
    this.autoRefreshTimer = null;

    if (enabled) {
      this.autoRefreshTimer = setInterval(() => {
        void this.readMemory(this.address, 256).then(data => {
          this.lines = this.buildDumpLines(this.address, data);
          this.update();
        });
      }, this.autoRefreshInterval);
    }

    this.autoRefreshBtn.textContent = 'Auto Refresh: ' + (enabled ? 'ON' : 'OFF');
    if (enabled) {
      this.autoRefreshBtn.classList.add('active');
    } else {
      this.autoRefreshBtn.classList.remove('active');
    }
  }

  private buildDumpLines(baseAddr: number, data: Uint8Array): MemoryDumpLine[] {
    const result: MemoryDumpLine[] = [];
    const count = Math.ceil(data.length / 16);

    for (let i = 0; i < count; i++) {
      const addr = baseAddr + i * 16;
      const bytes: number[] = [];
      let ascii = '';

      for (let j = 0; j < 16; j++) {
        const idx = i * 16 + j;
        if (idx < data.length) {
          const b = data[idx];
          bytes.push(b);
          ascii += (b >= 0x20 && b <= 0x7E) ? String.fromCharCode(b) : '.';
        } else {
          ascii += ' ';
        }
      }

      result.push({ address: addr, bytes, ascii });
    }

    return result;
  }

  render(): HTMLElement {
    this.update();
    return this.el;
  }

  update(): void {
    this.dumpEl.innerHTML = '';

    if (this.lines.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'memory-empty';
      empty.textContent = 'No memory data';
      this.dumpEl.appendChild(empty);
      return;
    }

    for (const line of this.lines) {
      const row = document.createElement('div');
      row.className = 'memory-line';

      const addrSpan = document.createElement('span');
      addrSpan.className = 'memory-addr';
      addrSpan.textContent = line.address.toString(16).padStart(6, '0').toUpperCase();
      addrSpan.style.backgroundColor = getRegionColor(line.address);
      row.appendChild(addrSpan);

      const hexSpan = document.createElement('span');
      hexSpan.className = 'memory-hex';
      const hexParts: string[] = [];
      for (let j = 0; j < 16; j++) {
        if (j < line.bytes.length) {
          hexParts.push(line.bytes[j].toString(16).padStart(2, '0').toUpperCase());
        } else {
          hexParts.push('  ');
        }
      }
      hexSpan.textContent = hexParts.join(' ');
      row.appendChild(hexSpan);

      const asciiSpan = document.createElement('span');
      asciiSpan.className = 'memory-ascii';
      asciiSpan.textContent = line.ascii;
      row.appendChild(asciiSpan);

      this.dumpEl.appendChild(row);
    }
  }

  destroy(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
